export type MantaServer = {
  id: string
  name: string
  host: string
  port: string
}

/**
 * Build the Manta connection string. The ws client derives its scheme (ws/wss)
 * from this string, so it MUST carry one — otherwise it falls back to insecure
 * `ws://`, which an HTTPS page blocks as mixed content. We honour a scheme that
 * the host already specifies, and otherwise pick wss on HTTPS pages and ws else.
 */
export const mantaUrl = ({
  host,
  port,
}: Pick<MantaServer, 'host' | 'port'>): string => {
  if (/^wss?:\/\//i.test(host)) return port ? `${host}:${port}` : host
  const scheme =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? 'wss'
      : 'ws'
  return port ? `${scheme}://${host}:${port}` : `${scheme}://${host}`
}

const SERVERS_KEY = 'mantaServers'
const SELECTED_KEY = 'selectedServerId'
const USERNAME_KEY = 'lastUsername'

const makeId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `srv-${Math.random().toString(36).slice(2)}`

export const loadServers = (): MantaServer[] => {
  try {
    const raw = localStorage.getItem(SERVERS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed as MantaServer[]
    }
  } catch {
    // ignore corrupt storage
  }

  // Migration: aus alten Einzel-Einträgen (host/port) einen Default-Server bauen.
  const legacyHost = localStorage.getItem('host') || window.location.hostname
  const legacyPort = localStorage.getItem('port') || '2311'
  const seed: MantaServer[] = [
    { id: makeId(), name: 'Lokal', host: legacyHost, port: legacyPort },
  ]
  saveServers(seed)
  return seed
}

export const saveServers = (servers: MantaServer[]): void => {
  localStorage.setItem(SERVERS_KEY, JSON.stringify(servers))
}

export const addServer = (server: Omit<MantaServer, 'id'>): MantaServer => {
  const servers = loadServers()
  const entry: MantaServer = { ...server, id: makeId() }
  saveServers([...servers, entry])
  return entry
}

export const removeServer = (id: string): MantaServer[] => {
  const servers = loadServers().filter((s) => s.id !== id)
  saveServers(servers)
  if (loadSelectedServerId() === id) localStorage.removeItem(SELECTED_KEY)
  return servers
}

export const loadSelectedServerId = (): string | null =>
  localStorage.getItem(SELECTED_KEY)

export const saveSelectedServerId = (id: string): void =>
  localStorage.setItem(SELECTED_KEY, id)

export const loadLastUsername = (): string =>
  localStorage.getItem(USERNAME_KEY) || ''

export const saveLastUsername = (username: string): void =>
  localStorage.setItem(USERNAME_KEY, username)
